// Wiederverwendbare OAuth-/Setup-Panels für Projekt-Integrationen.
// Ausgelagert aus IntegrationGrid.jsx, damit sie sowohl auf der
// Anbindungen-Seite als auch im Onboarding-Wizard genutzt werden können.
import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import api from '../../lib/axios';
import BookmarkletButton from '../BookmarkletButton';

export function relativeTime(iso) {
  if (!iso) return null;
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return 'gerade eben';
  if (diff < 3600) return `vor ${Math.floor(diff / 60)} Min.`;
  if (diff < 86400) return `vor ${Math.floor(diff / 3600)} Std.`;
  return `vor ${Math.floor(diff / 86400)} Tagen`;
}

export function GscConnectPanel({ integration, projectId }) {
  const qc = useQueryClient();
  const [connecting, setConnecting] = useState(false);
  const [sites, setSites] = useState(null);
  const [loadingSites, setLoadingSites] = useState(false);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [savedFlash, setSavedFlash] = useState(null);
  const [selectedSite, setSelectedSite] = useState(integration.config?.site_url || '');
  const isConnected = !!integration.config?.has_refresh_token || integration.config?.auth_mode === 'oauth';

  const loadSites = async () => {
    setLoadingSites(true);
    setError(null);
    try {
      const { data } = await api.get(`/projects/${projectId}/integrations/gsc/sites`, { skipProject: true });
      setSites(data.sites || []);
    } catch (e) {
      setError(e.response?.data?.message || e.message);
    } finally {
      setLoadingSites(false);
    }
  };

  const connect = async () => {
    setConnecting(true);
    setError(null);
    try {
      const { data } = await api.get(`/projects/${projectId}/integrations/gsc/authorize`, { skipProject: true });
      const popup = window.open(data.url, 'gsc_oauth', 'width=560,height=720');

      const handleMessage = (event) => {
        if (event.origin !== window.location.origin) return;
        const msg = event.data;
        if (!msg || msg.source !== 'gsc_oauth') return;
        window.removeEventListener('message', handleMessage);
        setConnecting(false);
        if (msg.status === 'success') {
          qc.invalidateQueries({ queryKey: ['project-integrations', projectId] });
          loadSites();
        } else {
          setError(msg.error || 'Verbindung fehlgeschlagen');
        }
      };
      window.addEventListener('message', handleMessage);

      // Fallback falls Popup geblockt / manuell geschlossen
      const pollTimer = setInterval(() => {
        if (popup?.closed) {
          clearInterval(pollTimer);
          setConnecting(false);
          window.removeEventListener('message', handleMessage);
          qc.invalidateQueries({ queryKey: ['project-integrations', projectId] });
        }
      }, 800);
    } catch (e) {
      setConnecting(false);
      setError(e.response?.data?.message || e.message);
    }
  };

  const saveSite = async () => {
    setError(null);
    setSavedFlash(null);
    setSaving(true);
    try {
      await api.post(
        `/projects/${projectId}/integrations/gsc/select-site`,
        { site_url: selectedSite },
        { skipProject: true }
      );
      qc.invalidateQueries({ queryKey: ['project-integrations', projectId] });
      qc.invalidateQueries({ queryKey: ['projects'] });
      setSavedFlash({ type: 'ok', text: `Property gespeichert: ${selectedSite}` });
      setTimeout(() => setSavedFlash(null), 3500);
    } catch (e) {
      setSavedFlash({ type: 'err', text: e.response?.data?.message || e.message });
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    if (isConnected && !sites) loadSites();
    // eslint-disable-next-line
  }, [isConnected]);

  return (
    <div className="space-y-4">
      {!isConnected ? (
        <>
          <p className="text-[12.5px] text-gray-600 leading-relaxed">
            Verbinde die Google Search Console dieses Kunden mit einem Klick. Du wirst zu Google weitergeleitet und wählst den Account aus, der die Property verwaltet.
          </p>
          <button
            onClick={connect}
            disabled={connecting}
            className="w-full flex items-center justify-center gap-2 bg-white border border-gray-300 rounded-lg py-2.5 text-[13px] font-medium hover:bg-gray-50 disabled:opacity-50"
          >
            <span className="w-4 h-4 rounded-full bg-gradient-to-br from-blue-500 via-red-500 to-yellow-500" />
            {connecting ? 'Warte auf Google…' : 'Mit Google verbinden'}
          </button>
        </>
      ) : (
        <>
          <div className="flex items-center gap-2 text-[12px] text-teal bg-teal-light px-3 py-2 rounded-lg">
            <span>✓</span>
            <span>Mit Google verbunden
              {integration.config?.connected_at && (
                <span className="text-gray-500"> · {relativeTime(integration.config.connected_at)}</span>
              )}
            </span>
          </div>

          <div>
            <label className="text-[12px] font-medium text-gray-700 block mb-1.5">
              Property für dieses Projekt
            </label>
            {loadingSites ? (
              <div className="text-[12px] text-gray-400 py-2">Lade Properties…</div>
            ) : (
              <select
                value={selectedSite}
                onChange={(e) => setSelectedSite(e.target.value)}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-[12.5px] bg-white outline-none focus:border-teal"
              >
                <option value="">— Property wählen —</option>
                {(sites || []).map((s) => (
                  <option key={s.site_url} value={s.site_url}>
                    {s.site_url} ({s.permission})
                  </option>
                ))}
              </select>
            )}
            <div className="flex gap-2 mt-2">
              <button
                onClick={saveSite}
                disabled={
                  saving ||
                  !selectedSite ||
                  selectedSite === integration.config?.site_url
                }
                className="flex-1 bg-teal text-white rounded-lg py-2 text-[12.5px] font-medium hover:opacity-90 disabled:opacity-50"
              >
                {saving ? 'Speichere…' : 'Speichern'}
              </button>
              <button
                onClick={loadSites}
                className="text-[12px] px-3 py-2 text-gray-600 hover:bg-gray-50 rounded-lg"
              >
                Neu laden
              </button>
            </div>
            {savedFlash && (
              <div
                className={`mt-2 text-[11.5px] rounded-md px-3 py-2 ${
                  savedFlash.type === 'ok'
                    ? 'bg-teal-light text-teal'
                    : 'bg-red-50 text-red-700'
                }`}
              >
                {savedFlash.type === 'ok' ? '✓ ' : '⚠ '}
                {savedFlash.text}
              </div>
            )}
          </div>

          <button
            onClick={connect}
            className="text-[11.5px] text-gray-500 hover:text-teal underline"
          >
            Mit anderem Google-Account neu verbinden
          </button>
        </>
      )}

      {error && (
        <div className="text-[11.5px] text-red-700 bg-red-50 rounded-md px-3 py-2">{error}</div>
      )}
    </div>
  );
}

export function Ga4ConnectPanel({ integration, projectId }) {
  const qc = useQueryClient();
  const [connecting, setConnecting] = useState(false);
  const [properties, setProperties] = useState(null);
  const [loadingProps, setLoadingProps] = useState(false);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [savedFlash, setSavedFlash] = useState(null);
  const [selectedProp, setSelectedProp] = useState(integration.config?.property_id || '');
  const isConnected = !!integration.config?.has_refresh_token;

  const loadProperties = async () => {
    setLoadingProps(true);
    setError(null);
    try {
      const { data } = await api.get(`/projects/${projectId}/integrations/ga4/properties`, { skipProject: true });
      setProperties(data.properties || []);
    } catch (e) {
      setError(e.response?.data?.message || e.message);
    } finally {
      setLoadingProps(false);
    }
  };

  const connect = async () => {
    setConnecting(true);
    setError(null);
    try {
      const { data } = await api.get(`/projects/${projectId}/integrations/ga4/authorize`, { skipProject: true });
      const popup = window.open(data.url, 'ga4_oauth', 'width=560,height=720');

      const handleMessage = (event) => {
        if (event.origin !== window.location.origin) return;
        const msg = event.data;
        if (!msg || msg.source !== 'ga4_oauth') return;
        window.removeEventListener('message', handleMessage);
        setConnecting(false);
        if (msg.status === 'success') {
          qc.invalidateQueries({ queryKey: ['project-integrations', projectId] });
          loadProperties();
        } else {
          setError(msg.error || 'Verbindung fehlgeschlagen');
        }
      };
      window.addEventListener('message', handleMessage);

      const pollTimer = setInterval(() => {
        if (popup?.closed) {
          clearInterval(pollTimer);
          setConnecting(false);
          window.removeEventListener('message', handleMessage);
          qc.invalidateQueries({ queryKey: ['project-integrations', projectId] });
        }
      }, 800);
    } catch (e) {
      setConnecting(false);
      setError(e.response?.data?.message || e.message);
    }
  };

  const saveProperty = async () => {
    setError(null);
    setSavedFlash(null);
    setSaving(true);
    try {
      await api.post(
        `/projects/${projectId}/integrations/ga4/select-property`,
        { property_id: selectedProp },
        { skipProject: true }
      );
      qc.invalidateQueries({ queryKey: ['project-integrations', projectId] });
      qc.invalidateQueries({ queryKey: ['projects'] });
      const label =
        (properties || []).find((p) => p.property_id === selectedProp)?.display_name ||
        selectedProp;
      setSavedFlash({ type: 'ok', text: `Property gespeichert: ${label}` });
      setTimeout(() => setSavedFlash(null), 3500);
    } catch (e) {
      setSavedFlash({ type: 'err', text: e.response?.data?.message || e.message });
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    if (isConnected && !properties) loadProperties();
    // eslint-disable-next-line
  }, [isConnected]);

  return (
    <div className="space-y-4">
      {!isConnected ? (
        <>
          <p className="text-[12.5px] text-gray-600 leading-relaxed">
            Verbinde Google Analytics 4 für dieses Projekt. Du wirst zu Google weitergeleitet und wählst den Account aus, der die GA4-Property verwaltet.
          </p>
          <button
            onClick={connect}
            disabled={connecting}
            className="w-full flex items-center justify-center gap-2 bg-white border border-gray-300 rounded-lg py-2.5 text-[13px] font-medium hover:bg-gray-50 disabled:opacity-50"
          >
            <span className="w-4 h-4 rounded-full bg-gradient-to-br from-blue-500 via-red-500 to-yellow-500" />
            {connecting ? 'Warte auf Google…' : 'Mit Google verbinden'}
          </button>
        </>
      ) : (
        <>
          <div className="flex items-center gap-2 text-[12px] text-teal bg-teal-light px-3 py-2 rounded-lg">
            <span>✓</span>
            <span>Mit Google verbunden
              {integration.config?.connected_at && (
                <span className="text-gray-500"> · {relativeTime(integration.config.connected_at)}</span>
              )}
            </span>
          </div>

          <div>
            <label className="text-[12px] font-medium text-gray-700 block mb-1.5">
              GA4 Property für dieses Projekt
            </label>
            {loadingProps ? (
              <div className="text-[12px] text-gray-400 py-2">Lade Properties…</div>
            ) : (
              <select
                value={selectedProp}
                onChange={(e) => setSelectedProp(e.target.value)}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-[12.5px] bg-white outline-none focus:border-teal"
              >
                <option value="">— Property wählen —</option>
                {(properties || []).map((p) => (
                  <option key={p.property_id} value={p.property_id}>
                    {p.display_name} · {p.account_name} ({p.property_id})
                  </option>
                ))}
              </select>
            )}
            <div className="flex gap-2 mt-2">
              <button
                onClick={saveProperty}
                disabled={saving || !selectedProp}
                className="flex-1 bg-teal text-white rounded-lg py-2 text-[12.5px] font-medium hover:opacity-90 disabled:opacity-50"
              >
                {saving ? 'Speichere…' : 'Speichern'}
              </button>
              <button
                onClick={loadProperties}
                className="text-[12px] px-3 py-2 text-gray-600 hover:bg-gray-50 rounded-lg"
              >
                Neu laden
              </button>
            </div>
            {savedFlash && (
              <div
                className={`mt-2 text-[11.5px] rounded-md px-3 py-2 ${
                  savedFlash.type === 'ok'
                    ? 'bg-teal-light text-teal'
                    : 'bg-red-50 text-red-700'
                }`}
              >
                {savedFlash.type === 'ok' ? '✓ ' : '⚠ '}
                {savedFlash.text}
              </div>
            )}
          </div>

          <button
            onClick={connect}
            className="text-[11.5px] text-gray-500 hover:text-teal underline"
          >
            Mit anderem Google-Account neu verbinden
          </button>
        </>
      )}

      {error && (
        <div className="text-[11.5px] text-red-700 bg-red-50 rounded-md px-3 py-2">{error}</div>
      )}
    </div>
  );
}

export function CroTrackerConnectPanel({ projectId }) {
  const qc = useQueryClient();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [enabling, setEnabling] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [verifyResult, setVerifyResult] = useState(null);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);

  const load = async () => {
    setError(null);
    try {
      const { data } = await api.get(
        `/projects/${projectId}/integrations/cro-tracker/status`,
        { skipProject: true }
      );
      setData(data);
    } catch (e) {
      setError(e.response?.data?.message || e.message);
    } finally {
      setLoading(false);
    }
  };

  const enable = async () => {
    setEnabling(true);
    setError(null);
    try {
      const { data } = await api.post(
        `/projects/${projectId}/integrations/cro-tracker/enable`,
        {},
        { skipProject: true }
      );
      setData(data);
      qc.invalidateQueries({ queryKey: ['project-integrations', projectId] });
    } catch (e) {
      setError(e.response?.data?.message || e.message);
    } finally {
      setEnabling(false);
    }
  };

  const runVerify = async () => {
    setVerifying(true);
    setVerifyResult(null);
    try {
      const { data } = await api.post(
        `/projects/${projectId}/integrations/cro_tracker/verify`,
        {},
        { skipProject: true }
      );
      setVerifyResult({
        status: data.integration?.status,
        error: data.integration?.last_error,
      });
      qc.invalidateQueries({ queryKey: ['project-integrations', projectId] });
      // Status-Daten ebenfalls neu laden
      await load();
    } catch (e) {
      setVerifyResult({ status: 'error', error: e.response?.data?.message || e.message });
    } finally {
      setVerifying(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [projectId]);

  if (loading) {
    return <div className="text-[12px] text-gray-400 py-4 text-center">Lade Tracking-Status…</div>;
  }

  const contact = data?.contact;
  const project = data?.project;
  const integration = data?.integration;
  const code = integration?.config?.tracking_code || contact?.tracking_code;
  const snippet = code
    ? `<script>(function(){var s=document.createElement('script');s.async=1;s.src='https://plattform.konversion.digital/api/track/${code}/loader.js';document.head.appendChild(s);})();</script>`
    : null;

  // 1) Kein Kontakt am Projekt
  if (!contact) {
    return (
      <div className="space-y-4">
        <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-lg px-3.5 py-3">
          <span className="text-amber-600 text-[15px]">⚠</span>
          <div className="flex-1 text-[12.5px] text-amber-800 leading-snug">
            Diesem Projekt ist noch <strong>kein Kontakt</strong> zugewiesen. Die Conversion
            Analyse läuft über den Kontakt des Projekts — weise in den Projekteinstellungen
            zuerst einen Kunden zu.
          </div>
        </div>
        <a
          href="/projects"
          className="w-full inline-flex items-center justify-center bg-teal text-white rounded-lg py-2.5 text-[13px] font-medium hover:opacity-90"
        >
          → Zu Projekten
        </a>
        {error && (
          <div className="text-[11.5px] text-red-700 bg-red-50 rounded-md px-3 py-2">{error}</div>
        )}
      </div>
    );
  }

  // 2) Kontakt vorhanden, aber Tracking noch nicht aktiviert
  if (!contact.tracking_enabled || !code) {
    return (
      <div className="space-y-4">
        <div className="bg-gray-50 rounded-lg px-3.5 py-3 border border-gray-100">
          <div className="text-[11px] text-gray-500 uppercase tracking-wide mb-1">Kontakt</div>
          <div className="text-[13px] font-semibold text-gray-900">{contact.name}</div>
          {contact.company && (
            <div className="text-[12px] text-gray-500">{contact.company}</div>
          )}
          {project?.domain && (
            <div className="text-[11.5px] text-gray-400 mt-1">Domain: {project.domain}</div>
          )}
        </div>

        <p className="text-[12.5px] text-gray-600 leading-relaxed">
          Mit einem Klick aktivieren wir Conversion-Tracking für diesen Kontakt, generieren einen
          projektspezifischen Tracking-Code und zeigen dir anschließend das Einbau-Snippet.
        </p>

        <button
          onClick={enable}
          disabled={enabling}
          className="w-full bg-teal text-white rounded-lg py-2.5 text-[13px] font-medium hover:opacity-90 disabled:opacity-50"
        >
          {enabling ? 'Aktiviere…' : `Tracking für ${contact.company || contact.name} aktivieren`}
        </button>

        {error && (
          <div className="text-[11.5px] text-red-700 bg-red-50 rounded-md px-3 py-2">{error}</div>
        )}
      </div>
    );
  }

  // 3) Tracking aktiv — Snippet anzeigen
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 text-[12px] text-teal bg-teal-light px-3 py-2 rounded-lg">
        <span>✓</span>
        <span>
          Tracking aktiv für <strong>{contact.company || contact.name}</strong> · Code{' '}
          <span className="font-mono">{code}</span>
        </span>
      </div>

      <div>
        <div className="text-[12px] font-medium text-gray-700 mb-1.5">
          1. Snippet ins &lt;head&gt; der Kundenseite einbauen
        </div>
        <pre className="bg-gray-900 text-gray-100 text-[11px] rounded-lg p-3 overflow-x-auto font-mono whitespace-pre-wrap break-all">
          {snippet}
        </pre>
        <button
          onClick={() => {
            navigator.clipboard.writeText(snippet);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
          }}
          className="mt-2 text-[12px] text-teal font-medium hover:underline"
        >
          {copied ? '✓ Kopiert' : 'In Zwischenablage kopieren'}
        </button>
      </div>

      <div className="text-[12px] text-gray-600 leading-snug">
        2. Anschließend „Einbau prüfen" — wir checken das HTML der Seite und die ersten Events.
      </div>

      <div className="border-t border-gray-100 pt-3">
        <div className="text-[12px] font-medium text-gray-700 mb-1.5">
          3. Conversions visuell taggen (optional)
        </div>
        <p className="text-[11.5px] text-gray-500 leading-snug mb-2">
          Ziehe das Bookmarklet in deine Lesezeichen-Leiste und klicke es auf der
          Kundenseite — du kannst dann beliebige Elemente per Mausklick als Conversion
          markieren (Buttons, Formulare, CTA-Links…).
        </p>
        <BookmarkletButton trackingCode={code} contactLabel={contact.company || contact.name} />
      </div>

      <button
        onClick={runVerify}
        disabled={verifying}
        className="w-full bg-gray-900 text-white rounded-lg py-2 text-[12.5px] font-medium hover:opacity-90 disabled:opacity-50"
      >
        {verifying ? 'Prüfe…' : 'Einbau prüfen'}
      </button>

      {verifyResult && (
        <div
          className={`text-[11.5px] rounded-md px-3 py-2 ${
            verifyResult.status === 'connected'
              ? 'bg-teal-light text-teal'
              : verifyResult.status === 'pending'
                ? 'bg-amber-50 text-amber-800'
                : 'bg-red-50 text-red-700'
          }`}
        >
          {verifyResult.status === 'connected' && '✓ Snippet erkannt und erste Events empfangen.'}
          {verifyResult.status === 'pending' && (verifyResult.error || 'Noch keine Events — ggf. Seite einmal öffnen.')}
          {(verifyResult.status === 'error' || verifyResult.status === 'inactive') &&
            (verifyResult.error || 'Prüfung fehlgeschlagen.')}
        </div>
      )}

      {!verifyResult && integration?.last_error && (
        <div className="text-[11.5px] text-amber-800 bg-amber-50 rounded-md px-3 py-2">
          Letzter Prüflauf: {integration.last_error}
        </div>
      )}
    </div>
  );
}
